import styled from "@emotion/styled";
import "maplibre-gl/dist/maplibre-gl.css";
import React from "react";
import { isLoading, Loading } from "../Loading";
import { decodePolylineString } from "../locationUtils";
import { Geo } from "../messages";
import { arrayAt } from "../utils/arrayUtils";
import { Layer, Marker, Source, useMap } from "react-map-gl/maplibre";
import { MAP_CAREGIVER_ALT_SVG } from "../utils/svgs";
import { AppIcon } from "./Generic/AppIcon";
import PatientAvatar from "./PatientAvatar/PatientAvatar";
import BaseMap, { BaseMapProps } from "./BaseMap";

interface Props {
  visitLocation: Geo;
  caregiverLocation: Geo;
  patientInitials: string;
  directionResult?: google.maps.DirectionsResult | Loading;
}

export const MapWithLocationsCenter = (props: Props) => {
  const overviewPolyline = (() => {
    const result = props.directionResult;

    if (result === undefined || isLoading(result)) {
      return;
    }

    return arrayAt(result.routes, 0)?.overview_polyline;
  })();

  const minLat = Math.min(props.caregiverLocation.lat, props.visitLocation.lat);
  const minLng = Math.min(props.caregiverLocation.lng, props.visitLocation.lng);
  const maxLat = Math.max(props.caregiverLocation.lat, props.visitLocation.lat);
  const maxLng = Math.max(props.caregiverLocation.lng, props.visitLocation.lng);

  const fitMapBounds: BaseMapProps["onLoad"] = (e) => {
    e.target.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 40, duration: 1000, maxZoom: 12, animate: true }
    );
  };

  const mapCenterLat =
    props.caregiverLocation.lat + (props.visitLocation.lat - props.caregiverLocation.lat) / 2;
  const mapCenterLng =
    props.caregiverLocation.lng + (props.visitLocation.lng - props.caregiverLocation.lng) / 2;

  return (
    <BaseMap
      destination={{ lat: mapCenterLat, lng: mapCenterLng }}
      zoom={12}
      onLoad={(e) => fitMapBounds(e)}
    >
      <Marker latitude={props.caregiverLocation.lat} longitude={props.caregiverLocation.lng}>
        <AppIcon icon={MAP_CAREGIVER_ALT_SVG} size={50} style={{ margin: "-25px -20px" }} />
      </Marker>

      <Marker latitude={props.visitLocation.lat} longitude={props.visitLocation.lng}>
        <MapWithLocationsCenter.PatientMarker>
          <PatientAvatar
            initials={props.patientInitials}
            className="AvailableVisitsList__visitIcon"
          />
        </MapWithLocationsCenter.PatientMarker>
      </Marker>

      {overviewPolyline !== undefined ? (
        <MapDirections
          origin={props.caregiverLocation}
          destination={props.visitLocation}
          overviewPolyline={overviewPolyline}
        />
      ) : null}
    </BaseMap>
  );
};

const MapDirections = (props: {
  origin: google.maps.LatLngLiteral;
  destination: google.maps.LatLngLiteral;
  overviewPolyline: string;
}) => {
  // Mabbox-gl needs the coordinates to be reversed (lng, lat instead of lat, lng) for some reason.
  const decodedPolyline = decodePolylineString(props.overviewPolyline).map((point) => [
    point[1],
    point[0],
  ]);
  const maxDecodedPolylinePoint = decodedPolyline.reduce(
    (max, point) => (point[0] > max[0] ? point : max),
    decodedPolyline[0]
  );

  const minDecodedPolylinePoint = decodedPolyline.reduce(
    (min, point) => (point[0] < min[0] ? point : min),
    decodedPolyline[0]
  );

  const map = useMap();

  map.current?.fitBounds(
    [
      [minDecodedPolylinePoint[0], minDecodedPolylinePoint[1]],
      [maxDecodedPolylinePoint[0], maxDecodedPolylinePoint[1]],
    ],
    { padding: 40, duration: 1000, maxZoom: 12, animate: true }
  );

  return (
    <Source
      id="map-directions-source"
      type="geojson"
      data={{
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: decodedPolyline,
        },
      }}
    >
      <Layer
        id="direction-route"
        type="line"
        paint={{ "line-opacity": 0.75, "line-width": 4, "line-color": "#3388ff" }}
      />
    </Source>
  );
};

export default MapWithLocationsCenter;

MapWithLocationsCenter.PatientMarker = styled.div`
  border-radius: 50% 50% 50% 0;
  border: 4px solid #fff;
  width: 40px;
  height: 40px;
  transform: rotate(-45deg);

  .PatientAvatar {
    transform: rotateZ(45deg);
  }

  margin-top: -40px;
  margin-left: -40px;
`;
